import type { Dispatch, SetStateAction } from "react";
import { formatMoney } from "@/lib/format";
import { Icon } from "@/components/Icon";
import "./AmountKeypad.css";

interface AmountKeypadProps {
  /** Raw digit string in cents, e.g. "1250" => $12.50. Single source of truth — no separate dollar value is tracked anywhere. */
  digits: string;
  /**
   * Must accept a FUNCTIONAL updater, not a plain value — see `handleKey`.
   * `useState`'s own setter satisfies this, so the parent passes it directly.
   */
  onDigitsChange: Dispatch<SetStateAction<string>>;
  maxDigits?: number;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clear", "0", "back"] as const;

/**
 * A tap-only numeric amount entry. Deliberately NOT a text/number <input> —
 * the task requires "constrained inputs only, no free-text fields" for the
 * quick-entry flow, and a keypad of buttons is unambiguously that, on top
 * of being faster to hit correctly with a thumb than a soft keyboard.
 *
 * Money stays an integer number of cents the whole time it lives in this
 * component and its parent — no float arithmetic, no parseFloat.
 */
export function AmountKeypad({ digits, onDigitsChange, maxDigits = 7 }: AmountKeypadProps) {
  const cents = digits === "" ? 0 : parseInt(digits, 10);
  const dollars = cents / 100; // display only — see lib/format.ts

  /**
   * Every branch uses a FUNCTIONAL updater, deliberately.
   *
   * The obvious version — `onDigitsChange(digits + key)` — reads `digits` from the
   * last render's props. Two taps landing inside one render cycle both see the same
   * stale value, so the second silently overwrites the first and a digit is LOST.
   *
   * That is not theoretical here. This flow is meant to take about ten seconds, so
   * people tap fast, and a farmer entering 412.90 could land 4290 without noticing.
   * Wrong amounts that look plausible are the worst possible failure for accounting
   * software to demo at a booth.
   *
   * Caught by driving the keypad programmatically: four rapid taps produced $0.00
   * because every one of them computed from the same empty string.
   *
   * The bounds checks moved inside the updater for the same reason — checking
   * `digits.length` out here reads the same stale snapshot.
   */
  function handleKey(key: (typeof KEYS)[number]): void {
    if (key === "back") {
      onDigitsChange((prev) => prev.slice(0, -1));
      return;
    }
    if (key === "clear") {
      onDigitsChange("");
      return;
    }
    onDigitsChange((prev) => {
      if (prev.length >= maxDigits) return prev;
      if (prev === "" && key === "0") return prev; // no leading zero to type through
      return prev + key;
    });
  }

  return (
    <div className="amount-keypad">
      <div className="amount-keypad__display" aria-live="polite">
        {formatMoney(dollars)}
      </div>
      <div className="amount-keypad__keys">
        {KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className="amount-keypad__key"
            onClick={() => handleKey(key)}
            aria-label={key === "back" ? "Delete digit" : key === "clear" ? "Clear amount" : key}
          >
            {key === "back" ? <Icon name="backspace" size={22} /> : key === "clear" ? "C" : key}
          </button>
        ))}
      </div>
    </div>
  );
}
